import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Trash2, Minus, Plus, ArrowLeft, Loader2, Lock, ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { formatPhone, digitsOnly, isValidUSPhone, toE164 } from "../lib/phone";
import errorMessage from "../lib/errors";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const CA_RATE = 0.0875;
const DELIVERY_FEE = 39.0;

export default function PartsCartPage() {
  const navigate = useNavigate();
  const { lines, setQty, remove, itemsSubtotal, clear } = useCart();
  const [fulfillment, setFulfillment] = useState("pickup");
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "CA", zip_code: "",
    sms_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  // If the cart is empty after a successful checkout (clear was called) the
  // user can hit Back; in that case re-route them to the catalog.
  useEffect(() => {
    if (lines.length === 0 && !clientSecret) {
      const t = setTimeout(() => navigate("/parts"), 50);
      return () => clearTimeout(t);
    }
  }, [lines.length, clientSecret, navigate]);

  const deliveryFee = fulfillment === "delivery" ? DELIVERY_FEE : 0;
  const taxable = itemsSubtotal + deliveryFee;
  const tax = useMemo(() => Math.round(taxable * CA_RATE * 100) / 100, [taxable]);
  const total = useMemo(() => Math.round((itemsSubtotal + deliveryFee + tax) * 100) / 100, [itemsSubtotal, deliveryFee, tax]);

  const setC = (patch) => setCustomer((c) => ({ ...c, ...patch }));
  const phoneValid = isValidUSPhone(customer.phone);
  const stateOk = (customer.state || "").toUpperCase() === "CA";
  const smsConsent = !!customer.sms_consent;
  const baseValid = customer.name && customer.email && phoneValid && smsConsent;
  const deliveryFieldsValid =
    fulfillment !== "delivery" ||
    (customer.address_line1 && customer.city && stateOk && customer.zip_code);
  const valid = baseValid && deliveryFieldsValid && lines.length > 0;

  const startPayment = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const orderRes = await api.post("/catalog/orders", {
        items: lines.map((l) => ({ slug: l.slug, quantity: l.quantity })),
        customer: { ...customer, phone: toE164(customer.phone) },
        fulfillment,
      });
      const order = orderRes.data;
      const sess = await api.post(`/orders/${order.id}/checkout-session`, {
        origin_url: window.location.origin,
      });
      if (!sess.data?.client_secret) throw new Error("Could not start checkout");
      // Stash order id so the thank-you page knows which to poll, then clear cart.
      sessionStorage.setItem("parts-last-order", order.id);
      clear();
      setClientSecret(sess.data.client_secret);
    } catch (e) {
      toast.error(errorMessage(e, "Checkout failed"));
      setSubmitting(false);
    }
  };

  if (clientSecret && stripePromise) {
    return (
      <div className="min-h-screen bg-[var(--brand-bg)]">
        <div className="max-w-2xl mx-auto px-5 py-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--brand-text)]">Payment</h1>
          <p className="muted-text mt-1 text-sm">Enter your card to complete the order.</p>
          <div className="rounded-2xl overflow-hidden border border-[var(--brand-border)] mt-4">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
          <div data-testid="trust-signals" className="mt-4 rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-bg-soft,#f8faf6)] px-4 py-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 mt-0.5 brand-text" />
                <div><p className="font-semibold text-[var(--brand-text)]">256-bit SSL</p></div>
              </div>
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 brand-text" />
                <div><p className="font-semibold text-[var(--brand-text)]">Powered by Stripe</p></div>
              </div>
              <div className="flex items-start gap-2">
                <CreditCard className="w-4 h-4 mt-0.5 brand-text" />
                <div><p className="font-semibold text-[var(--brand-text)]">PCI-compliant</p></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--brand-bg)]">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link to="/parts" data-testid="back-to-parts" className="inline-flex items-center gap-1 text-sm muted-text hover:brand-text">
          <ArrowLeft className="w-4 h-4" /> Back to parts
        </Link>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--brand-text)] mt-3">Your cart</h1>

        <div className="bg-white rounded-2xl border border-[var(--brand-border)] mt-4 divide-y divide-[var(--brand-border)]">
          {lines.map((l) => (
            <div key={l.slug} data-testid={`cart-line-${l.slug}`} className="flex items-center gap-3 p-4">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#f5f7f6] flex-shrink-0">
                {l.image_url && <img src={l.image_url} alt={l.name} className="w-full h-full object-contain p-1" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--brand-text)] truncate">{l.name}</p>
                <p className="text-xs muted-text">${Number(l.price).toFixed(2)} each</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`qty-down-${l.slug}`} onClick={() => setQty(l.slug, l.quantity - 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full border-2 border-[var(--brand-border)]">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-6 text-center font-semibold tabular-nums">{l.quantity}</span>
                <button data-testid={`qty-up-${l.slug}`} onClick={() => setQty(l.slug, l.quantity + 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full border-2 border-[var(--brand-border)]">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <span className="font-semibold text-[var(--brand-text)] w-16 text-right tabular-nums">
                ${(l.quantity * Number(l.price)).toFixed(2)}
              </span>
              <button data-testid={`remove-${l.slug}`} onClick={() => remove(l.slug)}
                className="text-red-600 p-1" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Fulfillment */}
        <div className="bg-white rounded-2xl border border-[var(--brand-border)] mt-4 p-4">
          <p className="text-xs font-bold uppercase tracking-wider muted-text mb-2">Fulfillment</p>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="fulfillment-pickup"
              onClick={() => setFulfillment("pickup")}
              className={`p-3 rounded-2xl text-sm font-semibold border-2 ${fulfillment === "pickup" ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]/40" : "border-[var(--brand-border)]"}`}>
              Pickup at shop · Free
            </button>
            <button data-testid="fulfillment-delivery"
              onClick={() => setFulfillment("delivery")}
              className={`p-3 rounded-2xl text-sm font-semibold border-2 ${fulfillment === "delivery" ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]/40" : "border-[var(--brand-border)]"}`}>
              Local delivery · ${DELIVERY_FEE}
            </button>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white rounded-2xl border border-[var(--brand-border)] mt-4 p-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider muted-text">Your info</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="cart-name" placeholder="Full name" value={customer.name}
              onChange={(e) => setC({ name: e.target.value })} className="rounded-input" />
            <input data-testid="cart-email" type="email" placeholder="Email" value={customer.email}
              onChange={(e) => setC({ email: e.target.value })} className="rounded-input" />
          </div>
          <input data-testid="cart-phone" type="tel" placeholder="(555) 123-4567"
            value={formatPhone(customer.phone)}
            onChange={(e) => setC({ phone: digitsOnly(e.target.value) })}
            className="rounded-input" />
          {customer.phone && !phoneValid && <p className="text-xs text-red-600">Enter a valid 10-digit US phone.</p>}
          {fulfillment === "delivery" && (
            <>
              <input data-testid="cart-address" placeholder="Address line 1" value={customer.address_line1}
                onChange={(e) => setC({ address_line1: e.target.value })} className="rounded-input" />
              <input placeholder="Address line 2 (optional)" value={customer.address_line2}
                onChange={(e) => setC({ address_line2: e.target.value })} className="rounded-input" />
              <div className="grid grid-cols-3 gap-3">
                <input data-testid="cart-city" placeholder="City" value={customer.city}
                  onChange={(e) => setC({ city: e.target.value })} className="rounded-input" />
                <input data-testid="cart-state" placeholder="CA" value={customer.state} maxLength={2}
                  onChange={(e) => setC({ state: e.target.value.toUpperCase() })} className="rounded-input" />
                <input data-testid="cart-zip" placeholder="ZIP" value={customer.zip_code}
                  onChange={(e) => setC({ zip_code: e.target.value })} className="rounded-input" />
              </div>
              {customer.state && !stateOk && <p className="text-xs text-amber-700">Local delivery is CA only — switch to pickup if you're outside CA.</p>}
            </>
          )}
        </div>

        {/* --- SMS consent (Twilio toll-free compliance) --- */}
        <label
          data-testid="parts-sms-consent-card"
          htmlFor="parts-sms-consent-checkbox"
          className={`flex gap-3 p-4 rounded-2xl border-2 cursor-pointer mt-4 transition-colors ${
            smsConsent
              ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]/30"
              : "border-[var(--brand-border)] bg-white"
          }`}
        >
          <input
            id="parts-sms-consent-checkbox"
            data-testid="parts-sms-consent-checkbox"
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setC({ sms_consent: e.target.checked })}
            className="mt-1 w-5 h-5 shrink-0 accent-[var(--brand-green)] cursor-pointer"
          />
          <span className="text-xs leading-relaxed text-[var(--brand-text)]">
            I agree to receive text messages from ScreenzZ regarding my order,
            scheduling, delivery, and customer support. Message &amp; data rates
            may apply. Reply <strong>STOP</strong> to opt out.{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-text font-semibold underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
            {" · "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-text font-semibold underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              Terms &amp; Conditions
            </a>
            .
          </span>
        </label>

        {/* Totals */}
        <div className="bg-white rounded-2xl border border-[var(--brand-border)] mt-4 p-4 text-sm">
          <Row label="Items subtotal" value={itemsSubtotal} />
          {fulfillment === "delivery" && <Row label="Local delivery" value={deliveryFee} />}
          <Row label={`Sales tax (${(CA_RATE * 100).toFixed(2)}% CA)`} value={tax} />
          <div className="flex items-baseline justify-between pt-2 mt-2 border-t border-[var(--brand-border)] font-extrabold text-[var(--brand-text)] text-base">
            <span>Total</span>
            <span data-testid="cart-total" className="tabular-nums">${total.toFixed(2)}</span>
          </div>
        </div>

        <button data-testid="parts-checkout-btn"
          onClick={startPayment} disabled={!valid || submitting}
          className="cta-btn w-full mt-5">
          {submitting ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading payment…</span> : "Continue to payment"}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="muted-text">{label}</span>
      <span className="tabular-nums text-[var(--brand-text)]">${Number(value).toFixed(2)}</span>
    </div>
  );
}
